import { useMutation, useQueryClient } from '@tanstack/react-query';

import { parseApiError } from '@cast/api-client';
import { notify } from '@cast/design-system';
import { apiClient } from '@cast/infrastructure-api';
import {
  KentFeature,
  KentFeatureConfig,
  KentFeatureConfigUpdate,
} from '@cast/types-kent';

import { mapKentFeatureDtoToKentFeature } from 'features/kent/utils';
import { getClusterFeaturesQueryKey } from 'hooks/queries/kent/useGetClusterFeaturesQuery';
import { getClusterOverviewQueryKey } from 'hooks/queries/kent/useGetClusterOverview';
import { getFeaturesConfigsQueryKey } from 'hooks/queries/kent/useGetFeaturesConfigsQuery';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { Cluster } from 'types/cluster';
import { notReachable } from 'utils/types/not-reachable';

/* 
  feature config endpoint returns a feature DTO instead of a feature config DTO,
  so we need to map the feature DTO to a feature config DTO in order for optimistic UI to work
*/
const mapKentFeatureToKentFeatureConfig = (
  feature: KentFeature
): KentFeatureConfig => {
  switch (feature.type) {
    case 'CLM':
    case 'POD_MUTATIONS':
    case 'REBALANCING':
    case 'WORKLOAD_RIGHT_SIZING':
    case 'SPOT_INTERRUPTION_PREDICTION':
      return {
        id: feature.id,
        type: feature.type,
        status: feature.status,
      };
    case 'CONTINUOUS_REBALANCING':
      return {
        id: feature.id,
        type: feature.type,
        status: feature.status,
        config: feature.continuousRebalancing.config,
      };
    case 'FEATURE_TYPE_UNSPECIFIED':
      return {
        type: 'FEATURE_TYPE_UNSPECIFIED',
      };
    default:
      return notReachable(feature);
  }
};

type RequestParams = {
  clusterId: Cluster['id'];
  featureConfig: KentFeatureConfigUpdate;
};

type Params = {
  onSuccess?: (updatedFeature: KentFeature) => void;
};

export const useConfigureFeatureMutation = ({ onSuccess }: Params = {}) => {
  const { currentOrganization } = useOrganizations();
  const organizationId = currentOrganization!.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clusterId, featureConfig }: RequestParams) => {
      return apiClient.kent.configureClusterFeature({
        organizationId,
        clusterId,
        featureConfigUpdate: featureConfig,
      });
    },

    onSuccess: ({ data: kentFeatureDto }, variables) => {
      const updatedFeature = mapKentFeatureDtoToKentFeature(kentFeatureDto);

      /* Invalidate Kent cluster overview and features queries since they include feature configs */
      queryClient.invalidateQueries({
        queryKey: getClusterOverviewQueryKey(variables.clusterId),
      });
      queryClient.invalidateQueries({
        queryKey: getClusterFeaturesQueryKey(variables.clusterId),
      });

      queryClient.setQueriesData(
        {
          queryKey: getFeaturesConfigsQueryKey(variables.clusterId),
        },
        (previousConfigs: KentFeatureConfig[] | undefined) => {
          if (!previousConfigs) {
            return previousConfigs;
          }

          const updatedConfigs: KentFeatureConfig[] = previousConfigs.map(
            (config) => {
              if (config.type === updatedFeature.type) {
                return mapKentFeatureToKentFeatureConfig(updatedFeature);
              }
              return config;
            }
          );

          return updatedConfigs;
        }
      );

      onSuccess?.(updatedFeature);
    },
    onError: (error) => {
      notify.error(parseApiError(error));
    },
  });
};
